<template>
    <fieldset>
        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Länk</div>
            </div>
            <input
                class="form-control"
                required
                readonly
                :value="route('integrations.adversus.createQuote')"
            />
        </div>

        <p class="small">
            <code>leadId</code> är återförsöksnyckeln. Samma lead och samma data returnerar den första offerten utan att skicka meddelanden igen; ändrad data för samma lead avvisas.
        </p>

        <p>Använd följande dataformat i JSON:</p>
        <pre><code>
                POST {{ route('integrations.adversus.createQuote') }}
                Authorization: Bearer {{ uuid }}

                {
                name: String,
                phone: String,
                email: String|null,
                leadId: Number,
                campaignId: String|null,
                donorId: String|null,
                productId1: String|null|"-", // "-", "--" eller "---" raderar paketet i denna position
                productId2: String|null|"-",
                productId3: String|null|"-",
                productId4: String|null|"-",
                salespersonEmail: String|null, // e-post till säljaren, måste tillhöra en användare i systemet
                }
            </code></pre>

        <div class="mb-3">
            <h6>Standardsäljare</h6>
            <v-select
                :options="usersForSelect"
                placeholder="Använd mallens säljare.."
                label="label"
                :reduce="(u) => u.value"
                v-model="defaultSalesId"
                @update:modelValue="onUpdate"
            ></v-select>
            <p class="small italic">
                Används när inkommande <code>salespersonEmail</code> saknas eller inte
                matchar någon användare. Lämnas tom används mallens säljare.
            </p>
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Mall ID</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="quoteTemplateId"
                @input="onUpdate"
            />
        </div>

        <hr class="my-4">

        <h6>Fältmappning</h6>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Adversus Lead ID</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="fieldAdversusLeadId"
                @input="onUpdate"
                placeholder="t.ex. adversus_lead_id"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Adversus Campaign ID (valfritt)</div>
            </div>
            <input
                type="text"
                class="form-control"
                v-model="fieldAdversusCampaignId"
                @input="onUpdate"
                placeholder="t.ex. adversus_campaign_id"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Adversus Donor ID (valfritt)</div>
            </div>
            <input
                type="text"
                class="form-control"
                v-model="fieldAdversusDonorId"
                @input="onUpdate"
                placeholder="t.ex. adversus_donor_id"
            />
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from "vue";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
});
const {
  modelValue
} = toRefs(props);
const emit = defineEmits(["update:modelValue"]);
const uuid = ref(null);
const quoteTemplateId = ref(null);
const fieldAdversusLeadId = ref(null);
const fieldAdversusCampaignId = ref(null);
const fieldAdversusDonorId = ref(null);
const defaultSalesId = ref(null);
const availableUsers = ref(null);
const serializedValue = computed(() => {
  return {
    uuid: uuid.value,
    quote_template_id: quoteTemplateId.value,
    field_adversus_lead_id: fieldAdversusLeadId.value,
    field_adversus_campaign_id: fieldAdversusCampaignId.value,
    field_adversus_donor_id: fieldAdversusDonorId.value,
    default_sales_id: defaultSalesId.value
  };
});
const usersForSelect = computed(() => {
  return (availableUsers.value ?? []).map(user => ({
    label: `${user.name} - ${user.email}`,
    value: user.id
  }));
});
function onUpdate() {
    emit("update:modelValue", serializedValue.value);
}
watch(modelValue, () => {
  if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
    uuid.value = modelValue.value.uuid;
    quoteTemplateId.value = modelValue.value.quote_template_id;
    fieldAdversusLeadId.value = modelValue.value.field_adversus_lead_id ?? modelValue.value.field_adversus_contact_id;
    fieldAdversusCampaignId.value = modelValue.value.field_adversus_campaign_id;
    fieldAdversusDonorId.value = modelValue.value.field_adversus_donor_id;
    defaultSalesId.value = modelValue.value.default_sales_id ?? null;
  }
  if (!uuid.value) {
    uuid.value = uuidv4();
  }
  if (defaultSalesId.value === undefined) {
    defaultSalesId.value = null;
  }
  if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
    onUpdate();
  }
}, {
  immediate: true
});
onMounted(async () => {
  const usersResponse = await axios.get(route("company.users"));
  availableUsers.value = usersResponse.data.sort((a, b) => a.name.localeCompare(b.name));
});
</script>
